import { Direction, Page, SwitchCondition } from "src/pageDefinitions/types";
import { CoreReduxState } from "src/utils/redux/store";
import {
  isBaseline,
  isFromEmail,
  isEmailMain,
  isEmailWinback,
  isInApp,
  isInApp14DayiOS,
  isInAppWinback,
  isApp14DayBaseRoute,
  isWinback,
  isCoreWinback,
  isEmailBlast,
} from "@utils/userSegment";
import * as FeatureSegments from "@utils/userSegment/features";
import invariant from "tiny-invariant";

const Segments: Record<string, (coreReduxState: CoreReduxState) => boolean> = {
  isBaseline,

  isFromEmail,
  isEmailMain,
  isEmailBlast,

  isInApp,
  isInApp14DayiOS,
  isInAppWinback,
  isApp14DayBaseRoute,

  isWinback,
  isCoreWinback,
  isEmailWinback,

  ...FeatureSegments,
};

export type PageCondition = {
  key?: string;
  operator?: keyof typeof PageConditionOperators;
  value: number | string | Array<number | string>;

  or?: PageCondition;
};

const PageConditionOperators: Record<
  string,
  (params: {
    lookupValue: (string | number | boolean | null)[];
    scalarValue: AnyJson;
    condition: PageCondition;
    state: CoreReduxState;
  }) => boolean
> = {
  EXISTS({ scalarValue }) {
    return scalarValue != null;
  },
  IN({ scalarValue, condition }) {
    invariant(
      Array.isArray(condition.value),
      "IN operator requires an array of values"
    );
    return condition.value.includes(scalarValue);
  },
  NOT_IN(params) {
    return !PageConditionOperators.IN(params);
  },
  CONTAINS({ lookupValue, condition: { value } }) {
    return lookupValue?.includes(value as string);
  },
  NOT_CONTAINS(params) {
    return !PageConditionOperators.CONTAINS(params);
  },
  CONTAINS_ANY({ lookupValue, condition: { value } }) {
    invariant(Array.isArray(value), "IN operator requires an array of values");
    return (
      lookupValue?.find((curr) => value.includes(curr as string | number)) !=
      null
    );
  },
  CONTAINS_NONE(params) {
    return !PageConditionOperators.CONTAINS_ANY(params);
  },
  EQUALS({ scalarValue, condition }) {
    return scalarValue == null
      ? condition.value == null
      : scalarValue === condition.value;
  },
  NOT_EQUALS(params) {
    return !PageConditionOperators.EQUALS(params);
  },
  GTE({ scalarValue, condition }) {
    return scalarValue >= condition.value;
  },
  LTE({ scalarValue, condition }) {
    return scalarValue <= condition.value;
  },
  ANY_SEGMENT({ condition: { value }, state }) {
    if (Array.isArray(value)) {
      return !!value.find((segmentName) => Segments[segmentName](state));
    }
    return Segments[value](state);
  },
  NO_SEGMENTS(params) {
    return !PageConditionOperators.ANY_SEGMENT(params);
  },
};

// TODO: Allow for conditional resolution of props?

/**
 * Handles the processing of PageConditions.
 *
 * @returns an opaque numeric score that can be used to compare different sets of passing conditions.
 */
export function conditionsPass(
  conditions: PageCondition[],
  state: CoreReduxState,
  direction = Direction.FORWARD
): number {
  if (!conditions?.length) {
    return 1;
  }

  function evaluateCondition(condition: PageCondition): number {
    const { operator = "EQUALS", key } = condition;

    const lookupValue =
      key === "direction" ? direction : resolveValue(state, key);

    const evaled = PageConditionOperators[operator]({
      condition,
      state,
      lookupValue,
      scalarValue: resolveScalar(lookupValue),
    });
    resolveScalar(lookupValue);

    let ret = evaled ? 1 : 0;
    if (condition.or) {
      ret += evaluateCondition(condition.or);
    }

    return ret;
  }

  const evaluatedConditions: number[] = conditions.map(evaluateCondition);
  const conditionScore = evaluatedConditions.reduce(
    (accum, value) => accum + value
  );

  return conditionScore ? conditionScore + 1 : 0;
}

export function resolveSwitch(
  switchCondition: SwitchCondition,
  state: CoreReduxState,
  direction = Direction.FORWARD
): Page {
  const { switch: key } = switchCondition;
  const lookupValue =
    key === "direction" ? direction : resolveValue(state, key);

  return (
    switchCondition.cases[lookupValue] || switchCondition.cases.default || {}
  );
}

function resolveValue(object: JsonObject, key: string): AnyJson {
  if (!key) {
    return null;
  }

  const tokens = key.split(".");
  let currentObject = object;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (currentObject[token] == null) {
      return null;
    }
    currentObject = currentObject[token];
  }
  return currentObject;
}
function resolveScalar(object: AnyJson) {
  if (Array.isArray(object)) {
    return object[0];
  }
  return object;
}
